import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { GUESS_CHECK, GUESS_STATUS, WeatherPayload, dayLabel } from '../../utils/weather';
import { Chip, ChipTone, FOCUS, NOTE } from './weatherUi';

// Claude's words: the week-ahead note and the explanation, in serif body text
// with the line that says who wrote them, and the guesses in their own small
// card. Claude writes words only; every number on the page came from code.

type Note = WeatherPayload['note'];
type Guess = WeatherPayload['guesses'][number];

const BY_CLAUDE = 'Written by Claude from the numbers on this page.';
const STATUS_TONE: Record<string, ChipTone> = { open: 'plum', held_up: 'busier', did_not_hold_up: 'quiet', unclear: 'quiet' };

export function ClaudeNote({ note }: { note: Note }) {
  const paras = [note?.week_ahead_note, note?.explanation].filter((p): p is string => !!p);
  if (!paras.length) return null;
  const claude = note?.by === 'claude';
  return (
    <section aria-labelledby="weather-words" className="rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card">
      <h2 id="weather-words" className="font-serif text-lg text-[#2D2A2E]">{claude ? "In Claude's words" : 'In a few words'}</h2>
      <div className="mt-3 max-w-[65ch] space-y-3">
        {paras.map((p) => <p key={p} className="font-serif text-base leading-[1.7] text-[#2D2A2E]">{p}</p>)}
      </div>
      {claude && <p className={`${NOTE} mt-3`}>{BY_CLAUDE}</p>}
    </section>
  );
}

function GuessRow({ g }: { g: Guess }) {
  const [open, setOpen] = useState(false);
  const id = `guess-${g.made_on}-${g.text.length}`;
  return (
    <li className="py-3">
      <button
        type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
        className={`flex w-full flex-col items-start gap-2 rounded-[8px] text-left sm:flex-row sm:justify-between sm:gap-3 ${FOCUS}`}
      >
        <span className="font-serif text-base leading-[1.6] text-[#2D2A2E]">{g.text}</span>
        <span className="flex shrink-0 items-center gap-1.5 sm:mt-1">
          <Chip tone={STATUS_TONE[g.status] ?? 'quiet'} kind="status">{GUESS_STATUS[g.status] ?? GUESS_STATUS.open}</Chip>
          <ChevronDown size={14} aria-hidden="true" className={`text-[#6B6670] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div id={id} className={`${NOTE} mt-1.5`}>
          <p>Made {dayLabel(g.made_on)}. {GUESS_CHECK[g.status] ?? GUESS_CHECK.open}</p>
        </div>
      )}
    </li>
  );
}

export function GuessesCard({ guesses }: { guesses: Guess[] }) {
  if (!guesses.length) return null;
  return (
    <section aria-labelledby="weather-guesses" className="rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card">
      <h2 id="weather-guesses" className="font-serif text-lg text-[#2D2A2E]">Guesses</h2>
      <p className={`${NOTE} mt-0.5`}>Claude&rsquo;s guesses about why, checked by code on later days. Tap one for its check.</p>
      <ul className="mt-1 divide-y divide-[#E6E2DE]">
        {guesses.map((g) => <GuessRow key={`${g.made_on}-${g.text}`} g={g} />)}
      </ul>
    </section>
  );
}
